import React from 'react';
import {Tag, Timeline, Typography} from 'antd';
import {useTranslation} from 'react-i18next';
import {PodExemptFlowType} from '../../graphql/podExempt/types';
import {POD_EXEMPT_FLOW_ACTION_I18N} from './podExemptConstants';

interface Props {
  flows: PodExemptFlowType[];
  /** 字典 code → 展示名（驳回码），缺失时回退 code */
  dictNameOf?: (code: string) => string;
  /** 审核页：待审单在末尾追加灰色"等待 X 审"节点（label + 已等待时长提示） */
  pendingNode?: {label: string; hint?: string};
}

/** 流转记录时间线（详情抽屉与审核页共用；数据源为 pod_exempt_flow，只读展示）。 */
const PodExemptFlowTimeline: React.FC<Props> = ({flows, dictNameOf, pendingNode}) => {
  const {t} = useTranslation();
  const nameOf = dictNameOf ?? ((code: string) => code);

  // 原型布局：动作标题一行，操作人·时间灰字第二行
  const items = flows.map((flow) => ({
        color: flow.action.includes('REJECT') || flow.action.includes('OFFLINE') ? 'red' : 'blue',
        children: (
          <div>
            <div className="text-gray-800">
              {t(POD_EXEMPT_FLOW_ACTION_I18N[flow.action] ?? flow.action)}
            </div>
            <div className="text-gray-400 text-xs">
              {[
                flow.operatorName,
                flow.createdAt ? new Date(flow.createdAt).toLocaleString() : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </div>
            {flow.rejectCodes.length > 0 && (
              // 驳回原因逐条竖排（与详情/审核页口径一致）
              <div className="mt-1 flex flex-col items-start gap-1.5">
                {flow.rejectCodes.map((code) => (
                  <Tag key={code} color="error" className="mr-0">
                    {nameOf(code)}
                  </Tag>
                ))}
              </div>
            )}
            {flow.opinion && <Typography.Text type="secondary">{flow.opinion}</Typography.Text>}
          </div>
        ),
      }));
  if (pendingNode) {
    items.push({
      color: 'gray',
      children: (
        <div>
          <div>
            <span className="font-medium text-gray-500">{pendingNode.label}</span>
          </div>
          {pendingNode.hint && (
            <Typography.Text type="secondary">{pendingNode.hint}</Typography.Text>
          )}
        </div>
      ),
    });
  }

  return <Timeline items={items} />;
};

export default PodExemptFlowTimeline;
